// pages/MyAttendance.jsx

import {
    Award,
    Calendar as CalendarIcon,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Clock,
    Coffee,
    FileText,
    User,
    XCircle,
    AlertCircle
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loading } from '../components/common/Loading';
import Navbar from '../components/header/Navbar';
import { useAuth } from '../hooks/useAuth';
import { getTodayString, getUserAttendanceHistory } from '../services/attendanceService';

const MyAttendance = () => {
    const { user, loading: isLoading } = useAuth();
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [viewMode, setViewMode] = useState('calendar');
    const [expandedDay, setExpandedDay] = useState(null);

    // Load attendance history
    useEffect(() => {
        if (!user) return;

        const loadData = async () => {
            try {
                const data = await getUserAttendanceHistory(user.uid, 90);
                setAttendance(data);
            } catch (error) {
                console.error('Error loading attendance:', error);
                toast.error('Failed to load attendance history');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [user]);

    // Get attendance for a specific date
    const getAttendanceForDate = (dateStr) => {
        return attendance.find(a => a.date === dateStr);
    };

    // Get status for a date
    const getDayStatus = (dateStr) => {
        const record = getAttendanceForDate(dateStr);
        if (!record) return 'absent';
        if (record.status === 'checked-out') return 'present';
        if (record.status === 'checked-in' || record.status === 'on-break') return 'in-progress';
        return 'absent';
    };

    // Generate calendar days
    const generateCalendarDays = () => {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDay = firstDay.getDay();

        const days = [];
        const today = getTodayString();

        for (let i = 0; i < startingDay; i++) {
            days.push({ date: null, empty: true });
        }

        for (let i = 1; i <= daysInMonth; i++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
            const status = getDayStatus(dateStr);
            const isToday = dateStr === today;
            const record = getAttendanceForDate(dateStr);

            days.push({
                date: i,
                dateStr,
                status,
                isToday,
                record
            });
        }

        return days;
    };

    // Calculate monthly stats
    const calculateMonthlyStats = () => {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth();
        const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

        const monthRecords = attendance.filter(a => a.date.startsWith(monthStr));
        const totalDays = monthRecords.length;
        const presentDays = monthRecords.filter(a => a.status === 'checked-out').length;
        const inProgress = monthRecords.filter(a => a.status === 'checked-in' || a.status === 'on-break').length;
        const absentDays = totalDays - presentDays - inProgress;

        // Calculate total hours from sessions
        let totalHours = 0;
        let totalOvertime = 0;
        let approvedOvertime = 0;
        let totalBreak = 0;
        let totalSessions = 0;

        monthRecords.forEach(record => {
            if (record.sessions) {
                totalSessions += record.sessions.length;
                record.sessions.forEach(session => {
                    totalHours += session.duration || 0;
                });
            }
            // Fallback for old records
            if (!record.sessions) {
                totalHours += record.workDuration || 0;
            }
            totalOvertime += record.overtime?.minutes || 0;
            approvedOvertime += record.overtime?.approved ? (record.overtime.minutes || 0) : 0;
            totalBreak += record.totalBreakTime || 0;
        });

        return {
            totalDays,
            presentDays,
            inProgress,
            absentDays,
            totalHours: Math.round(totalHours / 60),
            totalMinutes: Math.round(totalHours % 60),
            totalOvertime,
            approvedOvertime,
            totalBreak,
            totalSessions
        };
    };

    // Format date for display
    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Format duration
    const formatDuration = (minutes) => {
        if (!minutes) return '0h 0m';
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    // Get status badge
    const StatusBadge = ({ status, record }) => {
        const configs = {
            'present': {
                color: 'bg-green-100 text-green-700',
                icon: <CheckCircle className="w-3.5 h-3.5" />,
                label: 'Present'
            },
            'absent': {
                color: 'bg-red-100 text-red-700',
                icon: <XCircle className="w-3.5 h-3.5" />,
                label: 'Absent'
            },
            'in-progress': {
                color: 'bg-yellow-100 text-yellow-700',
                icon: <Clock className="w-3.5 h-3.5" />,
                label: 'In Progress'
            },
            'on-break': {
                color: 'bg-blue-100 text-blue-700',
                icon: <Coffee className="w-3.5 h-3.5" />,
                label: 'On Break'
            }
        };

        let statusKey = status;
        if (status === 'in-progress' && record?.status === 'on-break') {
            statusKey = 'on-break';
        }

        const config = configs[statusKey] || configs['absent'];
        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                {config.icon}
                {config.label}
            </span>
        );
    };

    // Render calendar cell
    const renderCalendarDay = (day) => {
        if (day.empty) {
            return <div className="h-16 bg-gray-50/50 rounded-lg"></div>;
        }

        const statusColors = {
            'present': 'bg-green-50 border-green-200 hover:bg-green-100',
            'absent': 'bg-red-50 border-red-200 hover:bg-red-100',
            'in-progress': 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
        };

        const statusDotColors = {
            'present': 'bg-green-500',
            'absent': 'bg-red-500',
            'in-progress': 'bg-yellow-500',
        };

        const isToday = day.isToday;
        const status = day.status;

        return (
            <div
                key={day.date}
                onClick={() => {
                    if (day.record) {
                        setExpandedDay(expandedDay === day.dateStr ? null : day.dateStr);
                    }
                }}
                className={`relative p-2 rounded-lg border-2 cursor-pointer transition-all ${statusColors[status] || 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    } ${isToday ? 'ring-2 ring-pink-400 ring-offset-2' : ''}`}
            >
                <div className="flex items-start justify-between">
                    <span className={`text-sm font-medium ${isToday ? 'text-pink-600' : 'text-gray-700'}`}>
                        {day.date}
                    </span>
                    {status !== 'absent' && (
                        <span className={`w-2 h-2 rounded-full ${statusDotColors[status] || 'bg-gray-400'}`}></span>
                    )}
                </div>

                {day.record && (
                    <div className="mt-1 text-xs text-gray-500">
                        {day.record.totalWorkDuration ? (
                            <span>
                                {formatDuration(day.record.totalWorkDuration)}
                            </span>
                        ) : day.status === 'in-progress' ? (
                            <span className="text-yellow-600">Active</span>
                        ) : null}
                        {day.record.overtime?.minutes > 0 && (
                            <span className="ml-1 text-orange-600">+{day.record.overtime.minutes}m</span>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Render expanded day details with sessions
    const renderExpandedDetails = (dateStr) => {
        const record = getAttendanceForDate(dateStr);
        if (!record) return null;

        const sessions = record.sessions || [];
        const totalWork = record.totalWorkDuration || 0;
        const totalBreak = record.totalBreakTime || 0;

        return (
            <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200 space-y-3">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-2 text-sm bg-gray-50 p-2 rounded-lg">
                    <div>
                        <span className="text-gray-500">Total Work:</span>
                        <span className="ml-2 font-medium text-blue-600">{formatDuration(totalWork)}</span>
                    </div>
                    <div>
                        <span className="text-gray-500">Total Break:</span>
                        <span className="ml-2 font-medium">{totalBreak}m</span>
                    </div>
                    <div>
                        <span className="text-gray-500">Sessions:</span>
                        <span className="ml-2 font-medium">{sessions.length}</span>
                    </div>
                    {record.overtime?.isOvertime && (
                        <div>
                            <span className="text-gray-500">Overtime:</span>
                            <span className={`ml-2 font-medium ${record.overtime.approved ? 'text-green-600' : 'text-orange-600'}`}>
                                {record.overtime.minutes}m
                                {!record.overtime.approved && ' (Pending)'}
                            </span>
                        </div>
                    )}
                </div>

                {/* Session Details */}
                {sessions.map((session, idx) => {
                    const checkInTime = session.checkIn?.time?.seconds
                        ? new Date(session.checkIn.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : '-';
                    const checkOutTime = session.checkOut?.time?.seconds
                        ? new Date(session.checkOut.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : 'Active';
                    const breakCount = session.breaks?.length || 0;
                    const breakTime = (session.breaks || [])
                        .filter(b => b.end)
                        .reduce((sum, b) => sum + (b.duration || 0), 0);

                    return (
                        <div key={session.id} className={`p-2 rounded-lg border ${!session.checkOut ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                            <div className="flex justify-between items-center">
                                <div>
                                    <span className="text-sm font-medium text-gray-800">
                                        Session #{idx + 1}
                                        {!session.checkOut && <span className="ml-2 text-xs text-green-600 font-normal">● Active</span>}
                                        {session.isOvertime && (
                                            <span className="ml-2 text-xs text-orange-600 font-normal">⏰ Overtime</span>
                                        )}
                                    </span>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        <span className="inline-flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {checkInTime} - {checkOutTime}
                                            {session.duration > 0 && ` (${formatDuration(session.duration)})`}
                                        </span>
                                        {breakCount > 0 && (
                                            <span className="ml-2 inline-flex items-center gap-1">
                                                <Coffee className="w-3 h-3" />
                                                {breakCount} break(s), {breakTime}m
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {session.checkOut && session.isOvertime && (
                                    <span className={`text-xs font-medium ${session.isOvertime?.approved ? 'text-green-600' : 'text-orange-600'}`}>
                                        {session.isOvertime?.approved ? '✓ Approved' : '⏳ Pending'}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}

                {/* Overtime Request Status */}
                {record.overtime?.requested && !record.overtime?.isOvertime && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                        <span className="text-xs text-yellow-700">
                            Overtime request pending approval
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const stats = calculateMonthlyStats();

    if (loading || isLoading) return <Loading />;

    return (
        <>
            <Navbar user={user} />
            <div className="max-w-6xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <User className="w-6 h-6" />
                            My Attendance
                        </h1>
                        <p className="text-sm text-gray-500">Track your attendance history and stats</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setViewMode('calendar')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${viewMode === 'calendar'
                                ? 'bg-pink-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            <CalendarIcon className="w-4 h-4 inline mr-1" />
                            Calendar
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${viewMode === 'list'
                                ? 'bg-pink-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            <FileText className="w-4 h-4 inline mr-1" />
                            List
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                        <p className="text-xs text-gray-500 font-medium">Present Days</p>
                        <p className="text-2xl font-bold text-green-600">{stats.presentDays}</p>
                        <p className="text-xs text-gray-400">Out of {stats.totalDays} days</p>
                    </div>
                    <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                        <p className="text-xs text-gray-500 font-medium">Total Hours</p>
                        <p className="text-2xl font-bold text-blue-600">{stats.totalHours}h {stats.totalMinutes}m</p>
                        <p className="text-xs text-gray-400">Worked this month</p>
                    </div>
                    <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                        <p className="text-xs text-gray-500 font-medium">Total Sessions</p>
                        <p className="text-2xl font-bold text-purple-600">{stats.totalSessions}</p>
                        <p className="text-xs text-gray-400">Check-in sessions</p>
                    </div>
                    <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                        <p className="text-xs text-gray-500 font-medium">Overtime</p>
                        <p className="text-2xl font-bold text-orange-600">{stats.totalOvertime}m</p>
                        <p className="text-xs text-gray-400">
                            {stats.approvedOvertime > 0 ? (
                                <span className="text-green-600">{stats.approvedOvertime}m approved</span>
                            ) : (
                                <span className="text-yellow-600">Pending approval</span>
                            )}
                        </p>
                    </div>
                    <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                        <p className="text-xs text-gray-500 font-medium">Break Time</p>
                        <p className="text-2xl font-bold text-indigo-600">{Math.round(stats.totalBreak / 60)}h</p>
                        <p className="text-xs text-gray-400">{Math.round(stats.totalBreak % 60)}m total breaks</p>
                    </div>
                </div>

                {/* Calendar View */}
                {viewMode === 'calendar' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        {/* Month Navigation */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-pink-100 rounded-lg">
                                    <CalendarIcon className="w-5 h-5 text-pink-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800">
                                    {selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                                </h3>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition"
                                >
                                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                                </button>
                                <button
                                    onClick={() => setSelectedMonth(new Date())}
                                    className="px-3 py-1.5 text-sm bg-pink-50 text-pink-600 rounded-lg hover:bg-pink-100 transition"
                                >
                                    Today
                                </button>
                                <button
                                    onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition"
                                >
                                    <ChevronRight className="w-5 h-5 text-gray-600" />
                                </button>
                            </div>
                        </div>

                        {/* Weekday Headers */}
                        <div className="grid grid-cols-7 gap-1 px-4 pt-4">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7 gap-1 p-4">
                            {generateCalendarDays().map((day, index) => (
                                <div key={index} className="min-h-[80px]">
                                    {renderCalendarDay(day)}
                                    {expandedDay === day.dateStr && renderExpandedDetails(day.dateStr)}
                                </div>
                            ))}
                        </div>

                        {/* Legend */}
                        <div className="px-6 py-3 border-t border-gray-100 flex flex-wrap items-center gap-4 text-xs">
                            <span className="text-gray-500 font-medium">Legend:</span>
                            <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                                Present
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                                In Progress
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                                Absent
                            </span>
                            <span className="flex items-center gap-1 text-gray-400">
                                <span className="w-3 h-3 rounded-full border-2 border-pink-400"></span>
                                Today
                            </span>
                        </div>
                    </div>
                )}

                {/* List View */}
                {viewMode === 'list' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <FileText className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800">Attendance History</h3>
                                    <p className="text-sm text-gray-500">Last 90 days</p>
                                </div>
                            </div>
                        </div>

                        {attendance.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <div className="text-4xl mb-2">📋</div>
                                <p className="font-medium">No attendance records found</p>
                                <p className="text-sm text-gray-400">You haven't checked in yet</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sessions</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Break</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Overtime</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {attendance.slice(0, 30).map((record) => {
                                            const sessionCount = record.sessions?.length || 0;
                                            const workMinutes = record.totalWorkDuration || 0;
                                            const hours = Math.floor(workMinutes / 60);
                                            const minutes = workMinutes % 60;
                                            const isPresent = record.status === 'checked-out';
                                            const isInProgress = record.status === 'checked-in' || record.status === 'on-break';

                                            let statusLabel = 'Absent';
                                            let statusColor = 'bg-red-100 text-red-700';
                                            if (isPresent) {
                                                statusLabel = 'Present';
                                                statusColor = 'bg-green-100 text-green-700';
                                            } else if (isInProgress) {
                                                statusLabel = 'In Progress';
                                                statusColor = 'bg-yellow-100 text-yellow-700';
                                            }

                                            return (
                                                <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 text-gray-700">
                                                        {formatDate(record.date)}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                                            {statusLabel}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-600">
                                                        {sessionCount > 0 ? `${sessionCount} session${sessionCount > 1 ? 's' : ''}` : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-600">
                                                        {workMinutes > 0 ? `${hours}h ${minutes}m` : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-600">
                                                        {record.totalBreakTime ? `${record.totalBreakTime}m` : '-'}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {record.overtime?.isOvertime ? (
                                                            <span className={`text-xs font-medium ${record.overtime.approved ? 'text-green-600' : 'text-orange-600'}`}>
                                                                {record.overtime.minutes}m
                                                                {!record.overtime.approved && ' (Pending)'}
                                                            </span>
                                                        ) : record.overtime?.requested ? (
                                                            <span className="text-xs text-yellow-600">⏳ Requested</span>
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {attendance.length > 30 && (
                                    <div className="px-4 py-3 border-t border-gray-200 text-center text-sm text-gray-500">
                                        Showing last 30 records out of {attendance.length}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default MyAttendance;

// // pages/MyAttendance.jsx

// import {
//     Calendar as CalendarIcon,
//     CheckCircle,
//     ChevronLeft,
//     ChevronRight,
//     Clock,
//     Coffee,
//     FileText,
//     User,
//     XCircle
// } from 'lucide-react';
// import { useEffect, useState } from 'react';
// import toast from 'react-hot-toast';
// import { Loading } from '../components/common/Loading';
// import Navbar from '../components/header/Navbar';
// import { useAuth } from '../hooks/useAuth';
// import { getTodayString, getUserAttendanceHistory } from '../services/attendanceService';

// const MyAttendance = () => {
//     const { user, loading: isLoading } = useAuth();
//     const [attendance, setAttendance] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [selectedMonth, setSelectedMonth] = useState(new Date());
//     const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
//     const [expandedDay, setExpandedDay] = useState(null);

//     // Load attendance history
//     useEffect(() => {
//         if (!user) return;

//         const loadData = async () => {
//             try {
//                 const data = await getUserAttendanceHistory(user.uid, 90); // Last 90 days
//                 setAttendance(data);
//             } catch (error) {
//                 console.error('Error loading attendance:', error);
//                 toast.error('Failed to load attendance history');
//             } finally {
//                 setLoading(false);
//             }
//         };

//         loadData();
//     }, [user]);

//     // Get attendance for a specific date
//     const getAttendanceForDate = (dateStr) => {
//         return attendance.find(a => a.date === dateStr);
//     };

//     // Get status for a date
//     const getDayStatus = (dateStr) => {
//         const record = getAttendanceForDate(dateStr);
//         if (!record) return 'absent';
//         if (record.status === 'checked-out') return 'present';
//         if (record.status === 'checked-in' || record.status === 'on-break') return 'in-progress';
//         return 'absent';
//     };

//     // Generate calendar days
//     const generateCalendarDays = () => {
//         const year = selectedMonth.getFullYear();
//         const month = selectedMonth.getMonth();
//         const firstDay = new Date(year, month, 1);
//         const lastDay = new Date(year, month + 1, 0);
//         const daysInMonth = lastDay.getDate();
//         const startingDay = firstDay.getDay(); // 0 = Sunday

//         const days = [];
//         const today = getTodayString();

//         // Empty cells for days before month starts
//         for (let i = 0; i < startingDay; i++) {
//             days.push({ date: null, empty: true });
//         }

//         // Actual days
//         for (let i = 1; i <= daysInMonth; i++) {
//             const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
//             const status = getDayStatus(dateStr);
//             const isToday = dateStr === today;
//             const record = getAttendanceForDate(dateStr);

//             days.push({
//                 date: i,
//                 dateStr,
//                 status,
//                 isToday,
//                 record
//             });
//         }

//         return days;
//     };

//     // Calculate monthly stats
//     const calculateMonthlyStats = () => {
//         const year = selectedMonth.getFullYear();
//         const month = selectedMonth.getMonth();
//         const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

//         const monthRecords = attendance.filter(a => a.date.startsWith(monthStr));
//         const totalDays = monthRecords.length;
//         const presentDays = monthRecords.filter(a => a.status === 'checked-out').length;
//         const inProgress = monthRecords.filter(a => a.status === 'checked-in' || a.status === 'on-break').length;
//         const absentDays = totalDays - presentDays - inProgress;
//         const totalHours = monthRecords.reduce((sum, a) => sum + (a.workDuration || 0), 0);
//         const totalOvertime = monthRecords.reduce((sum, a) => sum + (a.overtime?.minutes || 0), 0);
//         const approvedOvertime = monthRecords.reduce((sum, a) => sum + (a.overtime?.approved ? a.overtime.minutes : 0), 0);
//         const totalBreak = monthRecords.reduce((sum, a) => sum + (a.totalBreakTime || 0), 0);

//         return {
//             totalDays,
//             presentDays,
//             inProgress,
//             absentDays,
//             totalHours: Math.round(totalHours / 60),
//             totalMinutes: Math.round(totalHours % 60),
//             totalOvertime,
//             approvedOvertime,
//             totalBreak
//         };
//     };

//     // Format date for display
//     const formatDate = (dateStr) => {
//         const d = new Date(dateStr);
//         return d.toLocaleDateString('en-US', {
//             weekday: 'short',
//             month: 'short',
//             day: 'numeric',
//             year: 'numeric'
//         });
//     };

//     // Get status badge
//     const StatusBadge = ({ status, record }) => {
//         const configs = {
//             'present': {
//                 color: 'bg-green-100 text-green-700',
//                 icon: <CheckCircle className="w-3.5 h-3.5" />,
//                 label: 'Present'
//             },
//             'absent': {
//                 color: 'bg-red-100 text-red-700',
//                 icon: <XCircle className="w-3.5 h-3.5" />,
//                 label: 'Absent'
//             },
//             'in-progress': {
//                 color: 'bg-yellow-100 text-yellow-700',
//                 icon: <Clock className="w-3.5 h-3.5" />,
//                 label: 'In Progress'
//             },
//             'on-break': {
//                 color: 'bg-blue-100 text-blue-700',
//                 icon: <Coffee className="w-3.5 h-3.5" />,
//                 label: 'On Break'
//             }
//         };

//         let statusKey = status;
//         if (status === 'in-progress' && record?.status === 'on-break') {
//             statusKey = 'on-break';
//         }

//         const config = configs[statusKey] || configs['absent'];
//         return (
//             <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
//                 {config.icon}
//                 {config.label}
//             </span>
//         );
//     };

//     // Render calendar cell
//     const renderCalendarDay = (day) => {
//         if (day.empty) {
//             return <div className="h-16 bg-gray-50/50 rounded-lg"></div>;
//         }

//         const statusColors = {
//             'present': 'bg-green-50 border-green-200 hover:bg-green-100',
//             'absent': 'bg-red-50 border-red-200 hover:bg-red-100',
//             'in-progress': 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
//         };

//         const statusDotColors = {
//             'present': 'bg-green-500',
//             'absent': 'bg-red-500',
//             'in-progress': 'bg-yellow-500',
//         };

//         const isToday = day.isToday;
//         const status = day.status;

//         return (
//             <div
//                 key={day.date}
//                 onClick={() => {
//                     if (day.record) {
//                         setExpandedDay(expandedDay === day.dateStr ? null : day.dateStr);
//                     }
//                 }}
//                 className={`relative p-2 rounded-lg border-2 cursor-pointer transition-all ${statusColors[status] || 'bg-gray-50 border-gray-200 hover:bg-gray-100'
//                     } ${isToday ? 'ring-2 ring-pink-400 ring-offset-2' : ''}`}
//             >
//                 <div className="flex items-start justify-between">
//                     <span className={`text-sm font-medium ${isToday ? 'text-pink-600' : 'text-gray-700'}`}>
//                         {day.date}
//                     </span>
//                     {status !== 'absent' && (
//                         <span className={`w-2 h-2 rounded-full ${statusDotColors[status] || 'bg-gray-400'}`}></span>
//                     )}
//                 </div>

//                 {day.record && (
//                     <div className="mt-1 text-xs text-gray-500">
//                         {day.record.workDuration ? (
//                             <span>
//                                 {Math.floor(day.record.workDuration / 60)}h {Math.round(day.record.workDuration % 60)}m
//                             </span>
//                         ) : day.status === 'in-progress' ? (
//                             <span className="text-yellow-600">Active</span>
//                         ) : null}
//                         {day.record.overtime?.minutes > 0 && (
//                             <span className="ml-1 text-orange-600">+{day.record.overtime.minutes}m</span>
//                         )}
//                     </div>
//                 )}
//             </div>
//         );
//     };

//     // Render expanded day details
//     const renderExpandedDetails = (dateStr) => {
//         const record = getAttendanceForDate(dateStr);
//         if (!record) return null;

//         const checkInTime = record.checkIn?.time?.seconds
//             ? new Date(record.checkIn.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//             : '-';
//         const checkOutTime = record.checkOut?.time?.seconds
//             ? new Date(record.checkOut.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//             : '-';
//         const breakMinutes = record.totalBreakTime || 0;
//         const workMinutes = record.workDuration || 0;
//         const hours = Math.floor(workMinutes / 60);
//         const minutes = workMinutes % 60;

//         return (
//             <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200 space-y-2">
//                 <div className="grid grid-cols-2 gap-2 text-sm">
//                     <div>
//                         <span className="text-gray-500">Check In:</span>
//                         <span className="ml-2 font-medium">{checkInTime}</span>
//                     </div>
//                     <div>
//                         <span className="text-gray-500">Check Out:</span>
//                         <span className="ml-2 font-medium">{checkOutTime}</span>
//                     </div>
//                     <div>
//                         <span className="text-gray-500">Work Duration:</span>
//                         <span className="ml-2 font-medium text-blue-600">{hours}h {minutes}m</span>
//                     </div>
//                     <div>
//                         <span className="text-gray-500">Total Break:</span>
//                         <span className="ml-2 font-medium">{breakMinutes}m</span>
//                     </div>
//                     {record.overtime?.isOvertime && (
//                         <div className="col-span-2">
//                             <span className="text-gray-500">Overtime:</span>
//                             <span className={`ml-2 font-medium ${record.overtime.approved ? 'text-green-600' : 'text-orange-600'}`}>
//                                 {record.overtime.minutes}m
//                                 {!record.overtime.approved && ' (Pending Approval)'}
//                             </span>
//                         </div>
//                     )}
//                     {record.checkOut?.isAuto && (
//                         <div className="col-span-2 text-xs text-gray-400">
//                             <span className="flex items-center gap-1">
//                                 <Clock className="w-3 h-3" />
//                                 Auto checked out by system
//                             </span>
//                         </div>
//                     )}
//                     {record.breaks?.length > 0 && (
//                         <div className="col-span-2 border-t border-gray-100 pt-2">
//                             <span className="text-xs text-gray-500 block mb-1">Break Details:</span>
//                             {record.breaks.map((b, i) => (
//                                 <div key={i} className="text-xs text-gray-600">
//                                     #{i + 1}: {new Date(b.start.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
//                                     {b.end ? ` - ${new Date(b.end.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ' (Active)'}
//                                     {b.duration ? ` (${b.duration}m)` : ''}
//                                 </div>
//                             ))}
//                         </div>
//                     )}
//                 </div>
//             </div>
//         );
//     };

//     const stats = calculateMonthlyStats();

//     if (loading || isLoading) return <Loading />;

//     return (
//         <>
//             <Navbar user={user} />
//             <div className="max-w-6xl mx-auto px-4 py-6">
//                 {/* Header */}
//                 <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
//                     <div>
//                         <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
//                             <User className="w-6 h-6" />
//                             My Attendance
//                         </h1>
//                         <p className="text-sm text-gray-500">Track your attendance history and stats</p>
//                     </div>
//                     <div className="flex items-center gap-2">
//                         <button
//                             onClick={() => setViewMode('calendar')}
//                             className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${viewMode === 'calendar'
//                                 ? 'bg-pink-500 text-white'
//                                 : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
//                                 }`}
//                         >
//                             <CalendarIcon className="w-4 h-4 inline mr-1" />
//                             Calendar
//                         </button>
//                         <button
//                             onClick={() => setViewMode('list')}
//                             className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${viewMode === 'list'
//                                 ? 'bg-pink-500 text-white'
//                                 : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
//                                 }`}
//                         >
//                             <FileText className="w-4 h-4 inline mr-1" />
//                             List
//                         </button>
//                     </div>
//                 </div>

//                 {/* Stats Cards */}
//                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
//                     <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
//                         <p className="text-xs text-gray-500 font-medium">Present Days</p>
//                         <p className="text-2xl font-bold text-green-600">{stats.presentDays}</p>
//                         <p className="text-xs text-gray-400">Out of {stats.totalDays} days</p>
//                     </div>
//                     <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
//                         <p className="text-xs text-gray-500 font-medium">Total Hours</p>
//                         <p className="text-2xl font-bold text-blue-600">{stats.totalHours}h {stats.totalMinutes}m</p>
//                         <p className="text-xs text-gray-400">Worked this month</p>
//                     </div>
//                     <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
//                         <p className="text-xs text-gray-500 font-medium">Overtime</p>
//                         <p className="text-2xl font-bold text-orange-600">{stats.totalOvertime}m</p>
//                         <p className="text-xs text-gray-400">
//                             {stats.approvedOvertime > 0 ? (
//                                 <span className="text-green-600">{stats.approvedOvertime}m approved</span>
//                             ) : (
//                                 <span className="text-yellow-600">Pending approval</span>
//                             )}
//                         </p>
//                     </div>
//                     <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
//                         <p className="text-xs text-gray-500 font-medium">Break Time</p>
//                         <p className="text-2xl font-bold text-purple-600">{Math.round(stats.totalBreak / 60)}h</p>
//                         <p className="text-xs text-gray-400">{Math.round(stats.totalBreak % 60)}m total breaks</p>
//                     </div>
//                 </div>

//                 {/* Calendar View */}
//                 {viewMode === 'calendar' && (
//                     <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
//                         {/* Month Navigation */}
//                         <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
//                             <div className="flex items-center gap-3">
//                                 <div className="p-2 bg-pink-100 rounded-lg">
//                                     <CalendarIcon className="w-5 h-5 text-pink-600" />
//                                 </div>
//                                 <h3 className="text-lg font-semibold text-gray-800">
//                                     {selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
//                                 </h3>
//                             </div>
//                             <div className="flex gap-2">
//                                 <button
//                                     onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))}
//                                     className="p-2 hover:bg-gray-100 rounded-lg transition"
//                                 >
//                                     <ChevronLeft className="w-5 h-5 text-gray-600" />
//                                 </button>
//                                 <button
//                                     onClick={() => setSelectedMonth(new Date())}
//                                     className="px-3 py-1.5 text-sm bg-pink-50 text-pink-600 rounded-lg hover:bg-pink-100 transition"
//                                 >
//                                     Today
//                                 </button>
//                                 <button
//                                     onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))}
//                                     className="p-2 hover:bg-gray-100 rounded-lg transition"
//                                 >
//                                     <ChevronRight className="w-5 h-5 text-gray-600" />
//                                 </button>
//                             </div>
//                         </div>

//                         {/* Weekday Headers */}
//                         <div className="grid grid-cols-7 gap-1 px-4 pt-4">
//                             {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
//                                 <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
//                                     {day}
//                                 </div>
//                             ))}
//                         </div>

//                         {/* Calendar Grid */}
//                         <div className="grid grid-cols-7 gap-1 p-4">
//                             {generateCalendarDays().map((day, index) => (
//                                 <div key={index} className="min-h-[80px]">
//                                     {renderCalendarDay(day)}
//                                     {expandedDay === day.dateStr && renderExpandedDetails(day.dateStr)}
//                                 </div>
//                             ))}
//                         </div>

//                         {/* Legend */}
//                         <div className="px-6 py-3 border-t border-gray-100 flex flex-wrap items-center gap-4 text-xs">
//                             <span className="text-gray-500 font-medium">Legend:</span>
//                             <span className="flex items-center gap-1">
//                                 <span className="w-3 h-3 rounded-full bg-green-500"></span>
//                                 Present
//                             </span>
//                             <span className="flex items-center gap-1">
//                                 <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
//                                 In Progress
//                             </span>
//                             <span className="flex items-center gap-1">
//                                 <span className="w-3 h-3 rounded-full bg-red-500"></span>
//                                 Absent
//                             </span>
//                             <span className="flex items-center gap-1 text-gray-400">
//                                 <span className="w-3 h-3 rounded-full border-2 border-pink-400"></span>
//                                 Today
//                             </span>
//                         </div>
//                     </div>
//                 )}

//                 {/* List View */}
//                 {viewMode === 'list' && (
//                     <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
//                         <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
//                             <div className="flex items-center gap-3">
//                                 <div className="p-2 bg-blue-100 rounded-lg">
//                                     <FileText className="w-5 h-5 text-blue-600" />
//                                 </div>
//                                 <div>
//                                     <h3 className="text-lg font-semibold text-gray-800">Attendance History</h3>
//                                     <p className="text-sm text-gray-500">Last 90 days</p>
//                                 </div>
//                             </div>
//                         </div>

//                         {attendance.length === 0 ? (
//                             <div className="p-8 text-center text-gray-500">
//                                 <div className="text-4xl mb-2">📋</div>
//                                 <p className="font-medium">No attendance records found</p>
//                                 <p className="text-sm text-gray-400">You haven't checked in yet</p>
//                             </div>
//                         ) : (
//                             <div className="overflow-x-auto">
//                                 <table className="w-full text-sm">
//                                     <thead className="bg-gray-50 border-b border-gray-200">
//                                         <tr>
//                                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
//                                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
//                                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Check In</th>
//                                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Check Out</th>
//                                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
//                                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Break</th>
//                                             <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Overtime</th>
//                                         </tr>
//                                     </thead>
//                                     <tbody className="divide-y divide-gray-100">
//                                         {attendance.slice(0, 30).map((record) => {
//                                             const checkInTime = record.checkIn?.time?.seconds
//                                                 ? new Date(record.checkIn.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//                                                 : '-';
//                                             const checkOutTime = record.checkOut?.time?.seconds
//                                                 ? new Date(record.checkOut.time.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
//                                                 : '-';
//                                             const workMinutes = record.workDuration || 0;
//                                             const hours = Math.floor(workMinutes / 60);
//                                             const minutes = workMinutes % 60;
//                                             const isPresent = record.status === 'checked-out';
//                                             const isInProgress = record.status === 'checked-in' || record.status === 'on-break';

//                                             let statusLabel = 'Absent';
//                                             let statusColor = 'bg-red-100 text-red-700';
//                                             if (isPresent) {
//                                                 statusLabel = 'Present';
//                                                 statusColor = 'bg-green-100 text-green-700';
//                                             } else if (isInProgress) {
//                                                 statusLabel = 'In Progress';
//                                                 statusColor = 'bg-yellow-100 text-yellow-700';
//                                             }

//                                             return (
//                                                 <tr key={record.id} className="hover:bg-gray-50 transition-colors">
//                                                     <td className="px-4 py-3 text-gray-700">
//                                                         {formatDate(record.date)}
//                                                     </td>
//                                                     <td className="px-4 py-3">
//                                                         <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
//                                                             {statusLabel}
//                                                         </span>
//                                                     </td>
//                                                     <td className="px-4 py-3 text-gray-600">{checkInTime}</td>
//                                                     <td className="px-4 py-3 text-gray-600">
//                                                         {checkOutTime}
//                                                         {record.checkOut?.isAuto && (
//                                                             <span className="ml-1 text-xs text-gray-400">(auto)</span>
//                                                         )}
//                                                     </td>
//                                                     <td className="px-4 py-3 text-gray-600">
//                                                         {workMinutes > 0 ? `${hours}h ${minutes}m` : '-'}
//                                                     </td>
//                                                     <td className="px-4 py-3 text-gray-600">
//                                                         {record.totalBreakTime ? `${record.totalBreakTime}m` : '-'}
//                                                     </td>
//                                                     <td className="px-4 py-3">
//                                                         {record.overtime?.isOvertime ? (
//                                                             <span className={`text-xs font-medium ${record.overtime.approved ? 'text-green-600' : 'text-orange-600'}`}>
//                                                                 {record.overtime.minutes}m
//                                                                 {!record.overtime.approved && ' (Pending)'}
//                                                             </span>
//                                                         ) : (
//                                                             <span className="text-gray-400">-</span>
//                                                         )}
//                                                     </td>
//                                                 </tr>
//                                             );
//                                         })}
//                                     </tbody>
//                                 </table>
//                                 {attendance.length > 30 && (
//                                     <div className="px-4 py-3 border-t border-gray-200 text-center text-sm text-gray-500">
//                                         Showing last 30 records out of {attendance.length}
//                                     </div>
//                                 )}
//                             </div>
//                         )}
//                     </div>
//                 )}
//             </div>
//         </>
//     );
// };

// export default MyAttendance;